/**
 * NotificationDialog.jsx
 * Modal dialog used by the scheduler to create, edit and delete
 * exercise notification entries. Supports two user types:
 *  - regular users: select exercise preference(s) and a video is
 *    selected by preference
 *  - physio users: videos are selected/pinned by the physio flow
 *
 * Props:
 *  - open (bool): whether the dialog is open
 *  - onClose (fn): called when dialog should close
 *  - onSubmit (fn): called with the form payload to create/update
 *  - onDelete (fn): called with scheduleId when user confirms delete
 *  - exerciseTypes (array): list of available exercise type objects
 *  - initialData (object|null): existing event data when editing
 *  - isEditing (bool): whether the dialog is in edit mode
 *  - loading (bool): whether a save/delete is in progress
 *
 * The component contains form state, light validation, and helper
 * utilities to pick an appropriate video for previewing.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Typography,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Box,
  Skeleton,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";

import ActionDialog from "../ActionDialog";
import CalendarIcon from "../../assets/CalendarIcon.png";
import {
  getVideoByPreference,
  getRandomVideoForPhysio,
} from "../../util/videoHelpers";
import VideoComponent from "../../videoplayer/VideoComponent";
import { useVideosQuery } from "../../stores/tanstack/queries/useVideosQuery";
import { useAuthStore } from "../../stores/zustand";

const NotificationDialog = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  exerciseTypes = [],
  initialData = null,
  isEditing = false,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { data: videosData } = useVideosQuery();

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Video lists are provided by a tanstack query; default to empty arrays
  const allVideos = videosData?.allVideos || [];
  const physioVideos = videosData?.physioVideos || [];

  const { currentProduct } = useAuthStore();
  const isPhysioUser = currentProduct === "physio_user";

  // Check if this is a temporary event (optimistic update in progress)
  const isTempEvent =
    isPhysioUser &&
    initialData?.scheduleId &&
    typeof initialData.scheduleId === "string" &&
    initialData.scheduleId.startsWith("temp-");

  // Form state
  // Local UI state for the dialog inputs. Keep this minimal and
  // serializable so it is easy to convert to the API payload.
  const [dialogTitle, setDialogTitle] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [repeatPeriod, setRepeatPeriod] = useState("");
  const [selectOpen, setSelectOpen] = useState(false);
  const [videoToPlay, setVideoToPlay] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [repeatInterval, setRepeatInterval] = useState("NONE");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedExerciseTypes, setSelectedExerciseTypes] = useState([]);

  // Validation state
  const [errors, setErrors] = useState({});

  // Open the modal to play the video
  const handlePlayVideo = () => {
    // If this scheduled entry already has a video attached (physio notifications), play it directly.
    if (initialData?.video) {
      setVideoToPlay(initialData.video);
      setIsPlaying(true);
      return;
    }

    // Check if physio user
    const currentProduct = useAuthStore.getState().currentProduct;
    const isPhysioUser = currentProduct === "physio_user";

    if (isPhysioUser) {
      // Physio user - pick a deterministic/random video using the
      // physio selection helper and the scheduleId as a seed.
      const combinedVideos = [...allVideos, ...physioVideos];
      const matchingVideo = getRandomVideoForPhysio(
        combinedVideos,
        initialData?.scheduleId
      );

      if (matchingVideo) {
        setVideoToPlay(matchingVideo);
        setIsPlaying(true);
      } else {
        alert(t("NO Video Available") || "No video available");
      }
    } else {
      // Normal user - find by preference
      const firstExerciseType = selectedExerciseTypes[0];

      if (!firstExerciseType) {
        return;
      }

      const matchingVideo = getVideoByPreference(
        allVideos,
        firstExerciseType,
        initialData?.scheduleId
      );

      if (matchingVideo) {
        setVideoToPlay(matchingVideo);
        setIsPlaying(true);
      } else {
        alert(
          t("NO Video Available") || "No video available for this exercise type"
        );
      }
    }
  };

  // Close the video modal (stop playback)
  const handleCloseVideo = () => {
    setIsPlaying(false);
  };

  //  Initialize form when dialog opens (editing existing event)
  useEffect(() => {
    if (!open || !initialData) return;

    const date = initialData.date || "";
    const time = initialData.time || "";

    setSelectedDate(date);
    setSelectedTime(time);

    if (!isPhysioUser) {
      setSelectedExerciseTypes(initialData.exerciseTypes || []);
    } else {
      setSelectedExerciseTypes([]);
    }

    setRepeatInterval(initialData.repeatInterval || "NONE");
    setRepeatPeriod(initialData.repeatPeriod || "");

    if (initialData.video) {
      // physio type - use video title from notification
      setDialogTitle(initialData.video.title);
    } else if (isPhysioUser) {
      // Physio user event - get video with seed
      const combinedVideos = [...allVideos, ...physioVideos];
      const matchingVideo = getRandomVideoForPhysio(
        combinedVideos,
        initialData.scheduleId
      );
      setDialogTitle(matchingVideo?.title || initialData.name || "");
    } else {
      const isTempEvent =
        initialData.scheduleId &&
        typeof initialData.scheduleId === "string" &&
        initialData.scheduleId.startsWith("temp-");

      if (isTempEvent) {
        setDialogTitle("");
      } else if (initialData.exerciseTypes?.[0]) {
        const matchingVideo = getVideoByPreference(
          allVideos,
          initialData.exerciseTypes[0],
          initialData.scheduleId
        );
        setDialogTitle(matchingVideo?.title || initialData.name || "");
      } else {
        // Fallback
        setDialogTitle(initialData.name || "");
      }
    }

    setErrors({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData?.scheduleId]);

  //  Initialize form when dialog opens (creating new event)
  useEffect(() => {
    if (!open || initialData) return;

    // New notification defaults
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    const today = now.toISOString().split("T")[0];

    setSelectedDate(today);
    setSelectedTime(currentTime);

    const defaultTypes = exerciseTypes.length > 0 ? [exerciseTypes[0].id] : [];
    setSelectedExerciseTypes(defaultTypes);

    setRepeatInterval("NONE");
    setRepeatPeriod("");
    setDialogTitle(t("addExerciseEntry"));
    setErrors({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Update dialog title when initialData changes
  useEffect(() => {
    if (!open || !isEditing || !initialData) return;

    // Update title when initialData changes (happens when temp becomes real)
    if (initialData.video) {
      setDialogTitle(initialData.video.title);
    } else if (isPhysioUser) {
      const combinedVideos = [...allVideos, ...physioVideos];
      const matchingVideo = getRandomVideoForPhysio(
        combinedVideos,
        initialData.scheduleId
      );
      setDialogTitle(matchingVideo?.title || initialData.name || "");
    } else {
      const isTempEvent =
        initialData.scheduleId &&
        typeof initialData.scheduleId === "string" &&
        initialData.scheduleId.startsWith("temp-");

      if (isTempEvent) {
        setDialogTitle("");
      } else if (initialData.exerciseTypes?.[0]) {
        const matchingVideo = getVideoByPreference(
          allVideos,
          initialData.exerciseTypes[0],
          initialData.scheduleId
        );
        setDialogTitle(matchingVideo?.title || initialData.name || "");
      } else {
        setDialogTitle(initialData.name || "");
      }
    }
  }, [
    initialData?.scheduleId,
    initialData?.name,
    isPhysioUser,
    allVideos,
    physioVideos,
    open,
    isEditing,
    initialData,
  ]);

  const validateForm = () => {
    // Basic client-side validation; returns true when valid.
    const newErrors = {};

    if (!selectedDate) {
      newErrors.date = t("dateRequired");
    }

    if (!selectedTime) {
      newErrors.time = t("timeRequired");
    }

    if (!isPhysioUser && selectedExerciseTypes.length === 0) {
      newErrors.exerciseTypes = t("exerciseTypeRequired");
    }

    if (repeatInterval !== "NONE" && repeatPeriod) {
      const selectedDateValue = new Date(selectedDate).getTime();
      const repeatPeriodValue = new Date(repeatPeriod).getTime();

      if (repeatPeriodValue <= selectedDateValue) {
        newErrors.repeatPeriod = t("repeatPeriodAfterChosenDate");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    // Validate inputs before building the submission payload
    if (!validateForm()) {
      return;
    }

    const selectedDateTimeString = `${selectedDate}T${selectedTime}:00`;
    const localDate = new Date(selectedDateTimeString);

    if (isNaN(localDate.getTime())) {
      setErrors({ general: t("invalidDateTimeFormat") });
      return;
    }

    const notificationTimeISO = localDate.toISOString();

    let repeatPeriodISO = null;
    if (repeatInterval !== "NONE" && repeatPeriod && selectedTime) {
      const repeatDateTimeString = `${repeatPeriod}T${selectedTime}:00`;
      const localRepeatDate = new Date(repeatDateTimeString);
      if (!isNaN(localRepeatDate.getTime())) {
        repeatPeriodISO = localRepeatDate.toISOString();
      }
    }

    // Determine final preference IDs and event title based on user type
    // (physio users don't pick preferences the same way regular users do)
    const currentProduct = useAuthStore.getState().currentProduct;
    const isPhysioUser = currentProduct === "physio_user";

    // Handle preference IDs
    let finalPreferenceIds;
    let eventName;

    if (isPhysioUser) {
      // Physio user: always use fallback ID
      finalPreferenceIds = [-1];

      if (isEditing && dialogTitle) {
        // When editing, preserve the existing video title
        eventName = dialogTitle;
      } else {
        // When creating new event, use placeholder
        eventName = "Exercise";
      }
    } else {
      // Regular user: use selected preferences or fallback
      finalPreferenceIds =
        selectedExerciseTypes.length > 0 ? selectedExerciseTypes : [1];

      const firstPreferenceId = finalPreferenceIds[0];
      const firstPreference = exerciseTypes.find(
        (type) => type.id === firstPreferenceId
      );
      eventName = firstPreference?.name || dialogTitle;
    }

    const formData = {
      notificationDate: notificationTimeISO,
      notificationTime: notificationTimeISO,
      repeatInterval: repeatInterval !== "NONE" ? repeatInterval : "NONE",
      repeatPeriod: repeatPeriodISO,
      preferenceIds: finalPreferenceIds,
      date: selectedDate,
      time: selectedTime,
      name: eventName,
    };

    onSubmit(formData);
    handleClose();
  };

  // Close dialog and clear transient validation errors
  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const handleDelete = async () => {
    //  must be editing and have a scheduleId to delete
    if (!initialData?.scheduleId || !isEditing) {
      console.error("[NotificationDialog] Missing scheduleId or not editing");
      return;
    }

    handleClose();

    // Delete in background (useScheduler already handles optimistic update)
    try {
      await onDelete(initialData.scheduleId);
    } catch (error) {
      console.error("[NotificationDialog] Delete failed:", error);
    }
  };

  const handleDeleteConfirmed = () => {
    setShowDeleteConfirm(false);
    handleDelete();
  };

  const handleIntervalChange = (event, newInterval) => {
    if (newInterval !== null) {
      setRepeatInterval(newInterval);
      if (newInterval === "NONE") {
        setRepeatPeriod("");
      }
    }
  };

  const handleRepeatPeriodChange = (e) => {
    const newRepeatPeriod = e.target.value;
    setRepeatPeriod(newRepeatPeriod);

    if (errors.repeatPeriod) {
      setErrors((prev) => ({ ...prev, repeatPeriod: undefined }));
    }
  };

  return (
    <>
      <Dialog
        PaperProps={{ style: { borderRadius: 16 } }}
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <img
                src={CalendarIcon}
                className="icon-size"
                style={{
                  position: "relative",
                  width: "64px",
                  height: "64px",
                  marginRight: "24px",
                }}
                alt="Calendar"
              />
            </Grid>
            <Grid item xs>
              {isTempEvent || (!isPhysioUser && isEditing && !dialogTitle) ? (
                <Skeleton variant="text" width="60%" height={40} />
              ) : (
                <Typography variant="h6" style={{ color: "#063bd0" }}>
                  {isEditing ? dialogTitle : t("addExerciseEntry")}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<VideoLibraryIcon />}
                onClick={handlePlayVideo}
                disabled={
                  !isEditing ||
                  isTempEvent ||
                  (isEditing &&
                    !initialData?.video &&
                    !isPhysioUser &&
                    selectedExerciseTypes.length === 0)
                }
              >
                {t("Video") || "Watch Video"}
              </Button>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent>
          {isTempEvent ? (
            // Show skeleton while event is being created/updated
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" width="40%" height={30} />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={56}
                  sx={{ mt: 1, borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" width="40%" height={30} />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={56}
                  sx={{ mt: 1, borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={56}
                  sx={{ mt: 1, borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          ) : (
            // Show normal form when event is real
            <>
              {errors.general && (
                <Typography
                  color="error"
                  variant="body2"
                  style={{ marginBottom: 16 }}
                >
                  {errors.general}
                </Typography>
              )}

              <Grid container spacing={2}>
                {/* Date Selection */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" gutterBottom>
                    {t("date")}
                  </Typography>
                  <TextField
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      if (errors.date) {
                        setErrors((prev) => ({ ...prev, date: undefined }));
                      }
                    }}
                    fullWidth
                    error={!!errors.date}
                    helperText={errors.date}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Time Selection */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" gutterBottom>
                    {t("time")}
                  </Typography>
                  {isMobile ? (
                    <MobileTimePicker
                      value={
                        selectedTime
                          ? dayjs(`2000-01-01T${selectedTime}`)
                          : null
                      }
                      onChange={(newTime) => {
                        const timeString = newTime
                          ? newTime.format("HH:mm")
                          : "";
                        setSelectedTime(timeString);
                        if (errors.time) {
                          setErrors((prev) => ({ ...prev, time: undefined }));
                        }
                      }}
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.time,
                          helperText: errors.time,
                        },
                      }}
                    />
                  ) : (
                    <TimePicker
                      value={
                        selectedTime
                          ? dayjs(`2000-01-01T${selectedTime}`)
                          : null
                      }
                      onChange={(newTime) => {
                        const timeString = newTime
                          ? newTime.format("HH:mm")
                          : "";
                        setSelectedTime(timeString);
                        if (errors.time) {
                          setErrors((prev) => ({ ...prev, time: undefined }));
                        }
                      }}
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.time,
                          helperText: errors.time,
                          InputProps: { readOnly: true },
                          onFocus: (e) => {
                            e.target.readOnly = false;
                            e.target.blur();
                            e.target.readOnly = true;
                          },
                          onClick: (e) => {
                            e.target.readOnly = false;
                            e.target.blur();
                            e.target.focus();
                            e.target.readOnly = true;
                          },
                        },
                        hourMinute: { size: "small" },
                      }}
                    />
                  )}
                </Grid>

                {/* Exercise Type Selection */}
                {initialData?.type !== "PHYSIO" && !isPhysioUser && (
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!errors.exerciseTypes}>
                      <Typography variant="body2" gutterBottom>
                        {t("selectExerciseType")}
                      </Typography>
                      <Select
                        multiple
                        value={selectedExerciseTypes}
                        open={selectOpen}
                        onClose={() => setSelectOpen(false)}
                        onOpen={() => setSelectOpen(true)}
                        onChange={(e) => {
                          if (e.target.value.includes("all")) {
                            if (
                              selectedExerciseTypes.length ===
                              exerciseTypes.length
                            ) {
                              setSelectedExerciseTypes([]);
                            } else {
                              setSelectedExerciseTypes(
                                exerciseTypes.map((type) => type.id)
                              );
                            }
                          } else {
                            setSelectedExerciseTypes(e.target.value);
                          }

                          if (errors.exerciseTypes) {
                            setErrors((prev) => ({
                              ...prev,
                              exerciseTypes: undefined,
                            }));
                          }
                        }}
                        renderValue={(selected) => {
                          return exerciseTypes
                            .filter((type) => selected.includes(type.id))
                            .map((type) => type.name)
                            .join(", ");
                        }}
                      >
                        <MenuItem key="all" value="all">
                          <Checkbox
                            checked={
                              selectedExerciseTypes.length ===
                              exerciseTypes.length
                            }
                          />
                          {t("selectAll")}
                        </MenuItem>

                        {exerciseTypes.map((exerciseType) => (
                          <MenuItem
                            key={exerciseType.id}
                            value={exerciseType.id}
                          >
                            <Checkbox
                              checked={selectedExerciseTypes.includes(
                                exerciseType.id
                              )}
                            />
                            {exerciseType.name}
                          </MenuItem>
                        ))}

                        <div
                          key="confirm"
                          style={{ textAlign: "right", padding: "10px" }}
                        >
                          <Button onClick={() => setSelectOpen(false)}>
                            {t("okay")}
                          </Button>
                        </div>
                      </Select>
                      {errors.exerciseTypes && (
                        <FormHelperText>{errors.exerciseTypes}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {/* Repeat Interval */}
                <Grid item xs={12}>
                  <Typography variant="body2" gutterBottom>
                    {t("selectRepeatInterval")}
                  </Typography>
                  <ToggleButtonGroup
                    value={repeatInterval}
                    exclusive
                    onChange={handleIntervalChange}
                    aria-label="repeat interval"
                    fullWidth
                  >
                    <ToggleButton value="NONE" aria-label="none">
                      {t("none")}
                    </ToggleButton>
                    <ToggleButton value="DAILY" aria-label="daily">
                      {t("daily")}
                    </ToggleButton>
                    <ToggleButton value="WEEKLY" aria-label="weekly">
                      {t("weekly")}
                    </ToggleButton>
                    <ToggleButton value="MONTHLY" aria-label="monthly">
                      {t("monthly")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                {/* Repeat End Date */}
                {repeatInterval !== "NONE" && (
                  <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                      {t("repeatPeriod")}
                    </Typography>
                    <TextField
                      type="date"
                      value={repeatPeriod}
                      onChange={handleRepeatPeriodChange}
                      fullWidth
                      error={!!errors.repeatPeriod}
                      helperText={errors.repeatPeriod || t("repeatUntilHint")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2.5,
          }}
        >
          <Button
            variant="contained"
            onClick={() => setShowDeleteConfirm(true)}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              "&:hover": { backgroundColor: "#9a0007" },
            }}
            disabled={loading || !isEditing || isTempEvent}
          >
            {t("delete")}
          </Button>

          <Box sx={{ display: "flex", gap: 2.5 }}>
            <Button
              variant="contained"
              onClick={handleClose}
              disabled={loading}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || isTempEvent}
            >
              {loading ? t("saving") : isEditing ? t("update") : t("create")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {isPlaying && videoToPlay && (
        <Dialog
          open={isPlaying}
          onClose={handleCloseVideo}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: "#00023a",
              borderRadius: 16,
            },
          }}
        >
          <DialogContent sx={{ padding: 0, backgroundColor: "#00023a" }}>
            <VideoComponent
              videoId={videoToPlay.externalId}
              onClose={handleCloseVideo}
              playlist={[]}
            />
          </DialogContent>
        </Dialog>
      )}
      <ActionDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirmed}
        mode="confirm"
        title={t("areYouSureDeleteEvent")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
      />
    </>
  );
};

export default NotificationDialog;
